import { Button, Input } from 'ccs-ui-components';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NamespaceItemHandler from '../param-list-handler/NamespaceItemHandler';
import classes from './InputHandler.module.scss';
import { nonSpaceCharacterRegex } from '../../../../../constants';
import { useServiceDetailForm } from '../../../../../hooks';
import { INameSpaceParam } from '../../../../../models';

interface ItemListProps {
  listOfItems: (data: INameSpaceParam[]) => void;
  list?: INameSpaceParam[] | null;
  paramName?: string
}

const NamespaceInputHandler = ({ listOfItems, list = [], paramName = '' }: ItemListProps) => {
  const { handleInputChange, formData, setFormParamValues } = useServiceDetailForm();
  const [paramList, setParamList] = useState<INameSpaceParam[] | null | undefined>(list);
  const [updateObject, setUpdateObject] = useState<INameSpaceParam>();
  const { t: translate } = useTranslation();

  // Enabling add button based on validity
  const isAddBtnEnabled = () =>
    !!(
      formData.paramKey.isValid
      && formData.paramKey.value
      && nonSpaceCharacterRegex.test(formData.paramKey.value)
      && formData.namespace.isValid
      && formData.namespace.value
    );

  const SERVICE_VALIDATION_ERRORS = {
    prefixExists: translate('service_validation_error.prefixExists')
  };
  // On click of add button
  const onAddClickHandler = () => {
    const tempList = paramList ? [...paramList] : [];
    const tempObj = {
      prefix: formData.paramKey.value,
      namespace: formData.namespace.value,
      isEdit: false
    };
    tempList.push(tempObj);
    setParamList(tempList);
    formData.paramKey.value = '';
    formData.namespace.value = '';
    listOfItems(tempList);
  };

  // Checks if key already exists
  useEffect(() => {
    const newFormData = { ...formData };
    let status = false;
    if (isArray(paramList)) {
      paramList.forEach((item) => {
        if (formData.paramKey.value === item.prefix) {
          status = true;
        }
        return item;
      });
    }
    // const regExp = newFormData.paramKey.expression as RegExp;
    if (status) {
      newFormData.paramKey.isValid = false;
      newFormData.paramKey.isTouched = false;
      newFormData.paramKey.error = SERVICE_VALIDATION_ERRORS.prefixExists;
    } else if (
      !status
      // && regExp.test(newFormData.paramKey.value)
    ) {
      newFormData.paramKey.isValid = true;
      newFormData.paramKey.isTouched = true;
      newFormData.paramKey.error = '';
    }
    setFormParamValues(newFormData);
  }, [
    formData.paramKey.value,
    paramList?.length,
    paramList
  ]);

  // On update of any item in list
  const onItemChange = (obj: INameSpaceParam) => {
    setUpdateObject(obj);
  };

  useEffect(() => {
    const tempIndex = updateObject?.index ? updateObject.index : 0;
    if (updateObject) {
      const tempList = paramList ? [...paramList] : [];
      tempList.splice(tempIndex, 1, updateObject);
      setParamList(tempList);
      listOfItems(tempList);
    }
  }, [updateObject]);

  // on delete of any item in list
  const onItemDelete = (index: number) => {
    const tempData = paramList ? [...paramList] : [];
    tempData.splice(index, 1);
    setParamList(tempData);
    listOfItems(tempData);
  };

  return (
    <Row className={classes['main-container']}>
      <Form.Group>
        <Row className={classes['param-container']}>
          <Row className={classes['param-header']}>
            <Col sm={5}>
              {translate('service_details_form_component.lbl_prefix')}
            </Col>
            <Col sm={6} className={classes['namespace-value']}>
              {translate('service_details_form_component.lbl_param_namespace')}
            </Col>
          </Row>
          <Row className={classes['param-body']}>
            <Col sm={5}>
              <Input
                name="paramKey"
                label=""
                data-testid="prefix"
                placeholder={
                  String(translate(
                    'service_details_form_component.plchd_param_key'
                  ))
                }
                id="prefix"
                value={formData.paramKey.value}
                onChange={handleInputChange}
                error={!formData.paramKey.isValid}
                errorTxt={formData.paramKey.error}
                maxLength={Number.MAX_SAFE_INTEGER}
              />
            </Col>
            <Col sm={5}>
              <Input
                name="namespace"
                label=""
                data-testid="namespace"
                placeholder={String(
                  translate(
                    'service_details_form_component.plchd_param_value'
                  )
                )}
                value={formData.namespace.value}
                onChange={handleInputChange}
                id="namespace"
                error={!formData.namespace.isValid}
                errorTxt={formData.namespace.error ? String(translate(formData.namespace.error)) : ''}
                maxLength={Number.MAX_SAFE_INTEGER}
                className={classes['namespace-input']}
              />
            </Col>
            <Col sm={2}>
              <Button
                className={classes['add-button']}
                data-testid="add-namespace"
                variant="secondary"
                onClick={onAddClickHandler}
                disabled={!isAddBtnEnabled()}
              >
                {translate('service_operation_form.add_button')}
              </Button>
            </Col>
          </Row>
          {paramList
            && paramList?.length >= 1
            && paramList !== null
            && paramList.map((item, index) => (
              <NamespaceItemHandler
                index={index}
                key={item.prefix}
                itemKey={item.prefix}
                itemValue={item.namespace}
                isEdit={item.isEdit}
                isView={false} // must be false as this is edit/create component
                updateItem={onItemChange}
                deleteItem={onItemDelete}
                itemList={paramList}
              />
            ))}
          {paramList && paramList?.length < 1 && (
            <div className={classes['no-data']} data-testid="no-data">
              {paramName !== ''
                ? translate('service_details_form_component.plchd_no_records', {
                  replace: { param: paramName }
                }) : translate('service_details_form_component.plchd_no_records')}
            </div>
          )}
        </Row>
      </Form.Group>
    </Row>
  );
};
export default NamespaceInputHandler;
